import React, { useMemo } from 'react';
import { Transaction, Period, TransactionType } from '../types';
import { TransactionList } from '../components/TransactionList';
import { PeriodToggle } from '../components/PeriodToggle';
import { DateNavigation } from '../components/DateNavigation';
import { isSameDay, isSameMonth, isSameYear } from 'date-fns';
import { Filter } from 'lucide-react';
interface TransactionsPageProps {
  transactions: Transaction[];
  period: Period;
  date: Date;
  onPeriodChange: (p: Period) => void;
  onDateChange: (d: Date) => void;
  onEditTransaction: (t: Transaction) => void;
  typeFilter?: TransactionType | null;
  onFilterChange?: (filter: TransactionType | null) => void;
}
export function TransactionsPage({
  transactions,
  period,
  date,
  onPeriodChange,
  onDateChange,
  onEditTransaction,
  typeFilter,
  onFilterChange
}: TransactionsPageProps) {
  const filteredTransactions = useMemo(() => {
    let filtered = transactions.filter(t => {
      const tDate = new Date(t.date);
      if (period === 'day') return isSameDay(tDate, date);
      if (period === 'month') return isSameMonth(tDate, date);
      return isSameYear(tDate, date);
    });
    if (typeFilter) {
      filtered = filtered.filter(t => t.type === typeFilter);
    }
    return filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [transactions, period, date, typeFilter]);
  const handlePrevDate = () => {
    const newDate = new Date(date);
    if (period === 'day') newDate.setDate(date.getDate() - 1);
    if (period === 'month') newDate.setMonth(date.getMonth() - 1);
    if (period === 'year') newDate.setFullYear(date.getFullYear() - 1);
    onDateChange(newDate);
  };
  const handleNextDate = () => {
    const newDate = new Date(date);
    if (period === 'day') newDate.setDate(date.getDate() + 1);
    if (period === 'month') newDate.setMonth(date.getMonth() + 1);
    if (period === 'year') newDate.setFullYear(date.getFullYear() + 1);
    onDateChange(newDate);
  };
  return <div className="space-y-6 pb-24" data-id="element-478">
      <div className="bg-stone-900 sticky top-0 z-30 py-2 border-b border-stone-800/50 backdrop-blur-md bg-opacity-80" data-id="element-479">
        <h1 className="text-2xl font-bold text-stone-50 px-2 mb-4" data-id="element-480">Activity</h1>

        <div className="px-2 mb-3 flex gap-2 items-center" data-id="element-481">
          <Filter size={16} className="text-stone-500" data-id="element-482" />
          <div className="flex gap-2 flex-wrap" data-id="element-483">
            <button onClick={() => onFilterChange?.(null)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!typeFilter ? 'bg-amber-500 text-stone-900' : 'bg-stone-800 text-stone-400 hover:text-stone-200'}`} data-id="element-484">
              All
            </button>
            <button onClick={() => onFilterChange?.('income')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${typeFilter === 'income' ? 'bg-amber-500 text-stone-900' : 'bg-stone-800 text-stone-400 hover:text-stone-200'}`} data-id="element-485">
              Income
            </button>
            <button onClick={() => onFilterChange?.('expense')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${typeFilter === 'expense' ? 'bg-red-500 text-white' : 'bg-stone-800 text-stone-400 hover:text-stone-200'}`} data-id="element-486">
              Expenses
            </button>
            <button onClick={() => onFilterChange?.('savings')} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${typeFilter === 'savings' ? 'bg-emerald-500 text-white' : 'bg-stone-800 text-stone-400 hover:text-stone-200'}`} data-id="element-487">
              Savings
            </button>
          </div>
        </div>

        <PeriodToggle value={period} onChange={onPeriodChange} data-id="element-488" />
        <DateNavigation date={date} period={period} onPrev={handlePrevDate} onNext={handleNextDate} onDateClick={() => {}} onDateChange={onDateChange} data-id="element-489" />
      </div>

      <div className="px-1" data-id="element-490">
        <TransactionList transactions={filteredTransactions} onEdit={onEditTransaction} emptyMessage="No transactions in this period" data-id="element-491" />
      </div>
    </div>;
}